import {
  buildHtml,
  buildLink,
  EventPage,
  RouteInput,
  RouteSlotInput,
} from '@luks/ui';
import ical from 'ical-generator';
import { toBase64 } from 'js-base64';
import { ComponentProps } from 'react';

import { cleanList } from '../../commons/cleanList';
import { getDaysAgoTimestamp } from '../../commons/date';
import { helpfulness } from '../../commons/Helpfulness';
import { notEmpty } from '../../commons/not-empty';
import { removeEmptyParagraphs } from '../../commons/preprocess-paragraphs';
import { SEO } from '../../commons/Seo';
import mapPageMetadata from '../../commons/useMetatags';
import { mapBreadcrumbs } from '../Breadcrumbs';
import { buildDrupalImage } from '../Image';
import { mapParagraphs } from '../paragraphs/AllParagraphs';
import { mapWebform } from '../paragraphs/Webform';
import { mapSidebar } from '../Sidebar';

export function mapEventPage(
  input: EventPageFragment,
): RouteInput<typeof EventPage> {
  const calendar = ical({ name: 'LUKS' });
  const timezone = 'Europe/Zurich';
  calendar.createEvent({
    start: new Date(
      input.startTime
        ? `${input.startDate}T${input.startTime}`
        : `${input.startDate}T00:00:00`,
    ),
    end: new Date(
      input.endTime
        ? `${input.endDate}T${input.endTime}`
        : `${input.endDate}T23:59:59`,
    ),
    timezone: timezone,
    summary: input.title,
    description: input.lead,
    location: input.location,
    // Provide timestamp and ID. Otherwise, ical-generator
    // will take the current time and generate a UUID, which
    // will lead to unnecessary changes in generated HTML in
    // between the builds.
    stamp: new Date(input.createdTimestamp),
    id: input.drupalId,
  });
  const timezoneGenerator = () => `
BEGIN:VTIMEZONE
TZID:Europe/Zurich
X-LIC-LOCATION:Europe/Zurich
BEGIN:DAYLIGHT
TZOFFSETFROM:+0100
TZOFFSETTO:+0200
TZNAME:CEST
DTSTART:19700329T020000
RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=-1SU
END:DAYLIGHT
BEGIN:STANDARD
TZOFFSETFROM:+0200
TZOFFSETTO:+0100
TZNAME:CET
DTSTART:19701025T030000
RRULE:FREQ=YEARLY;BYMONTH=10;BYDAY=-1SU
END:STANDARD
END:VTIMEZONE`;
  calendar.timezone({
    name: timezone,
    generator: timezoneGenerator,
  });

  return {
    header: mapEventPageHeader(input),
    body: [
      {
        key: 'EventDetails',
        input: {
          eventStatus: input.eventStatus,
          startDate: input.startDate,
          startTime: input.startTime,
          endDate: input.endDate,
          endTime: input.endTime,
          CalendarLink: buildLink({
            href:
              'data:text/calendar;charset=utf8;base64,' +
              toBase64(calendar.toString(), true),
            download: 'event.ics',
          }),
          location: input.location || '',
          subjects:
            input.organizer?.filter(cleanList).map((it) => ({
              area: [it.title, it.subtitle].filter(notEmpty).join(', '),
              Link: buildLink({ href: it.path }),
            })) || [],
        },
      },
      {
        key: 'Figure',
        input: {
          Image: buildDrupalImage(input.image, 'content'),
        },
      },
      ...mapParagraphs(removeEmptyParagraphs(input.content)),
      ...(input.form ? [mapWebform(input.form)] : []),
      mapSidebar(input.sidebar),
      helpfulness(),
    ],
  };
}

export function mapEventPageHeader(
  input: EventPageFragment,
): RouteSlotInput<typeof EventPage, 'header'> {
  return {
    ...mapBreadcrumbs(input.breadcrumbs),
    Title: buildHtml(input.title),
    Description: buildHtml(input.lead || ''),
  };
}

export function mapEventPageMetadata(
  input: EventPageFragment,
): ComponentProps<typeof SEO> {
  const metadata = mapPageMetadata(input);
  if (
    Math.floor(
      Date.parse(
        // Timezone gets lost, but we don't care much about it here.
        input.endTime
          ? `${input.endDate}T${input.endTime}`
          : `${input.endDate}T00:00:00`,
      ) / 1000,
    ) < getDaysAgoTimestamp(1)
  ) {
    if (!metadata.meta) {
      metadata.meta = [];
    }
    metadata.meta.push({
      name: 'robots',
      content: 'noindex, nofollow, noimageindex',
      // @ts-ignore
      'data-no-index': 'true',
      key: 'no-index-ssr-key',
    });
  }
  return metadata;
}
